import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { CITIES, ALL_VA_CITIES } from "@/lib/data";
import ServiceMap from "@/components/ServiceMap";

export default function ServiceArea() {
  return (
    <section data-testid="service-area-section" id="service-area" className="section-pad">
      <div className="container-pad mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <span className="eyebrow">
              <span className="w-8 h-px bg-teal-500" />
              Service Area
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-light text-navy tracking-tight mt-5 leading-[1.15]">
              Serving patients across <strong className="font-bold text-teal-600">Virginia, DC & Maryland.</strong>
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-base md:text-lg text-navy/65 leading-relaxed font-light">
              From Northern Virginia to the District of Columbia and the Maryland suburbs, Comfort Care delivers
              safe, dependable, ADA-compliant medical transportation across the DMV region.
            </p>
          </div>
        </div>

        {/* Map + image */}
        <div className="grid lg:grid-cols-5 gap-6 mb-14">
          <div className="lg:col-span-3">
            <ServiceMap />
          </div>
          <div className="lg:col-span-2 grid grid-cols-2 lg:grid-cols-1 gap-4">
            <div className="rounded-3xl overflow-hidden border border-gray-100 relative group">
              <img
                src="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/r4712rd0_84EBC7D9-7177-4F0A-9C38-BA0A08E3B4F6.PNG"
                alt="Comfort Care Transportation van interior with deployed wheelchair ramp"
                className="w-full h-44 lg:h-44 object-cover group-hover:scale-105 transition duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent flex items-end p-4">
                <div className="text-white text-sm font-semibold">Our wheelchair-accessible fleet</div>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden border border-gray-100 relative group">
              <img
                src="https://customer-assets.emergentagent.com/job_comfort-care-booking/artifacts/l28afqe7_Screenshot%202026-06-05%20at%209.16.56%E2%80%AFAM.png"
                alt="Wheelchair user safely boarding accessible transportation van via ramp"
                className="w-full h-44 lg:h-44 object-cover group-hover:scale-105 transition duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent flex items-end p-4">
                <div className="text-white text-sm font-semibold">Safe wheelchair boarding</div>
              </div>
            </div>
          </div>
        </div>

        {/* City cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {CITIES.map((c) => (
            <Link
              to={`/service-area/${c.slug}`}
              key={c.slug}
              data-testid={`city-card-${c.slug}`}
              className={`card-soft p-6 group hover:-translate-y-1 relative ${c.isHub ? "ring-2 ring-teal-500" : ""}`}
            >
              {c.isHub && (
                <div className="absolute -top-3 left-6 bg-teal-500 text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full shadow-md">
                  Main Hub
                </div>
              )}
              <div className="flex items-center justify-between">
                <MapPin className={`w-5 h-5 ${c.isHub ? "text-teal-600" : "text-teal-500"}`} />
                <span className="text-[10px] uppercase tracking-widest text-navy/40 font-medium">{c.region}</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-navy mt-4">{c.name}</h3>
              <p className="text-navy/60 text-sm mt-2 leading-relaxed">{c.blurb}</p>
              <div className="flex items-center gap-1 text-teal-600 text-sm font-medium mt-4 opacity-0 group-hover:opacity-100 transition">
                Explore <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>

        <div className="bg-sand rounded-3xl p-8 md:p-12 border border-sand-200">
          <h3 className="font-heading text-xl font-semibold text-navy mb-5">All Communities We Serve</h3>
          <p className="text-sm text-navy/65 mb-4">Certified non-emergency medical transportation across <strong className="text-navy">Virginia, Washington DC, and Maryland</strong>.</p>
          <div className="flex flex-wrap gap-2">
            {ALL_VA_CITIES.map((city) => (
              <span key={city} className="px-4 py-1.5 rounded-full bg-white border border-gray-100 text-sm text-navy/75 hover:border-teal-300 hover:text-teal-700 transition cursor-default">
                {city}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
